import { useQuery } from '@tanstack/react-query';
import { apiWrapper } from '@/apiWrapper';
import { getEndpoints } from '@/endpoints';
import type { TimeSlot } from '@/features/appointments/utils/TimeSlot';

interface NearbyAgentsQueryParams {
  workTypeId: string;
  zipCode?: string;
  lat?: number;
  lng?: number;
  city?: string;
  state?: string;
}

interface UseNearbyAgentsQueryOptions {
  enabled?: boolean;
}

export interface NearbyAgentInfo {
  resourceId: string;
  name: string;
  agentRecordId: string;
  image: string;
  location: string;
  city?: string;
  state?: string;
  serviceTerritoryId: string;
  timeSlots: TimeSlot[];
  distance: number;
}

interface PaginatedNearbyAgentsResponse {
  results: NearbyAgentInfo[];
  pagination: {
    total: number;
    hasMore: boolean;
    nextOffset: number;
  };
}

const getNearbyAgents = async (
  params: NearbyAgentsQueryParams,
): Promise<NearbyAgentInfo[]> => {
  const url = getEndpoints().nearbyAgents.replace(
    '{workTypeId}',
    params.workTypeId,
  );
  const config = {
    method: 'GET',
    params: {
      ...(params.zipCode !== undefined && { zipCode: params.zipCode }),
      ...(params.lat !== undefined && { lat: params.lat }),
      ...(params.lng !== undefined && { lng: params.lng }),
      ...(params.city !== undefined && { city: params.city }),
      ...(params.state !== undefined && { state: params.state }),
      limit: 30,
      offset: 0,
    },
  };

  const response = await apiWrapper<PaginatedNearbyAgentsResponse>(url, config);

  return response.results ?? [];
};

export const useNearbyAgentsQuery = (
  params: NearbyAgentsQueryParams,
  options?: UseNearbyAgentsQueryOptions,
) => {
  const { enabled = true } = options || {};

  return useQuery({
    queryKey: [
      'nearbyAgents',
      params.workTypeId,
      params.zipCode,
      params.lat,
      params.lng,
      params.city,
      params.state,
    ],
    queryFn: () => getNearbyAgents(params),
    enabled,
    staleTime: 2 * 60 * 1000,
  });
};
